import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import '../styles/components/deploymentManagement.css';

interface PM2Service {
  name: string;
  status: string;
  uptime: number;
  restarts: number;
  cpu: number;
  memory: number;
}

interface DeploymentStatus {
  current_version: string;
  current_build_time: string;
  latest_version: string;
  latest_build_time: string;
  is_latest: boolean;
  pending_migrations: string[];
  has_pending_migrations: boolean;
}

interface Build {
  name: string;
  version: string;
  build: string;
  buildTime: string;
  is_current: boolean;
}

interface DeploymentManagementProps {
  token: string | null;
}

const DeploymentManagement: React.FC<DeploymentManagementProps> = ({ token }) => {
  const [services, setServices] = useState<PM2Service[]>([]);
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatus | null>(null);
  const [builds, setBuilds] = useState<Build[]>([]);
  const [restartingService, setRestartingService] = useState<string | null>(null);
  const [switchingBuild, setSwitchingBuild] = useState<string | null>(null);
  const [showBuilds, setShowBuilds] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPM2Status = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/admin/pm2/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setServices(data.services);
    } catch (e) {
      console.error('Failed to fetch PM2 status:', e);
    }
  };

  const fetchDeploymentStatus = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/admin/deployment/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setDeploymentStatus(data);
    } catch (e) {
      console.error('Failed to fetch deployment status:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBuilds = async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/admin/deployment/builds`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setBuilds(data.builds || []);
    } catch (e) {
      console.error('Failed to fetch builds:', e);
    }
  };

  const switchBuild = async (buildName: string) => {
    if (!token || switchingBuild) return;
    
    const confirmed = window.confirm(
      `Switch to build: ${buildName}?\n\n` +
      `The frontend will restart and users may experience a brief interruption.`
    );
    
    if (!confirmed) return;
    
    setSwitchingBuild(buildName);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/admin/deployment/switch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ build_name: buildName })
      });
      
      if (!resp.ok) {
        const error = await resp.json();
        throw new Error(error.error || 'Failed to switch build');
      }
      
      alert(`Successfully switched to ${buildName}!\n\nThe page will reload in 3 seconds.`);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (e: any) {
      alert(`Failed to switch build: ${e.message}`);
      setSwitchingBuild(null);
    }
  };

  const restartService = async (serviceName: string) => {
    if (!token || restartingService) return;
    
    const serviceLabel = serviceName === 'all' ? 'All Services' : serviceName;
    const confirmed = window.confirm(
      `Restart ${serviceLabel}?\n\n` +
      `This may cause brief downtime (~3-5 seconds).`
    );
    
    if (!confirmed) return;
    
    setRestartingService(serviceName);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/admin/pm2/restart/${serviceName}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!resp.ok) {
        let errorMessage = 'Failed to restart service';
        try {
          const error = await resp.json();
          errorMessage = error.error || errorMessage;
        } catch {
          // Response is not JSON (likely HTML error page)
          errorMessage = `Server error (${resp.status}): ${resp.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      alert(`${serviceLabel} restarted successfully!`);
      setTimeout(() => {
        fetchPM2Status();
        fetchDeploymentStatus();
      }, 2000);
    } catch (e: any) {
      alert(`Failed to restart: ${e.message}`);
    } finally {
      setRestartingService(null);
    }
  };

  const formatUptime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${seconds}s`;
  };

  const formatMemory = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  useEffect(() => {
    if (token) {
      fetchPM2Status();
      fetchDeploymentStatus();
      fetchBuilds();
      
      // Refresh every 30 seconds
      const interval = setInterval(() => {
        fetchPM2Status();
        fetchDeploymentStatus();
        fetchBuilds(); // Also refresh builds list
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [token]);

  if (loading) {
    return <div className="deployment-loading">Loading deployment status...</div>;
  }

  return (
    <div className="deployment-management">
      {/* Version Status */}
      {deploymentStatus && (
        <div className="version-status">
          <div className="version-header">
            <h4>Deployment Status</h4>
            {!deploymentStatus.is_latest && (
              <span className="version-warning">⚠️ Not on latest build</span>
            )}
            {deploymentStatus.is_latest && (
              <span className="version-ok">✓ Latest build</span>
            )}
          </div>
          
          <div className="version-info">
            <div className="version-row">
              <span className="version-label">Current:</span>
              <span className="version-value">
                v{deploymentStatus.current_version}
                {deploymentStatus.current_build_time && (
                  <span className="build-time"> ({new Date(deploymentStatus.current_build_time).toLocaleString()})</span>
                )}
              </span>
            </div>
            
            {!deploymentStatus.is_latest && deploymentStatus.latest_version && (
              <div className="version-row">
                <span className="version-label">Latest:</span>
                <span className="version-value">
                  v{deploymentStatus.latest_version}
                  {deploymentStatus.latest_build_time && (
                    <span className="build-time"> ({new Date(deploymentStatus.latest_build_time).toLocaleString()})</span>
                  )}
                </span>
              </div>
            )}
          </div>
          
          {/* Build Management */}
          {builds.length > 1 && (
            <div className="build-management">
              <button 
                className="toggle-builds-btn"
                onClick={() => setShowBuilds(!showBuilds)}
              >
                {showBuilds ? '▼' : '▶'} Available Builds ({builds.length})
              </button>
              
              {showBuilds && (
                <div className="builds-list">
                  {builds.map((build) => (
                    <div key={build.name} className={`build-item ${build.is_current ? 'current' : ''}`}>
                      <div className="build-info">
                        <div className="build-version">
                          v{build.version} (Build {build.build})
                          {build.is_current && <span className="current-badge">CURRENT</span>}
                        </div>
                        <div className="build-time">
                          {new Date(build.buildTime).toLocaleString()}
                        </div>
                      </div>
                      {!build.is_current && (
                        <button
                          className="switch-btn"
                          onClick={() => switchBuild(build.name)}
                          disabled={!!switchingBuild}
                        >
                          {switchingBuild === build.name ? 'Switching...' : 'Switch'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Migration Warning */}
          {deploymentStatus.has_pending_migrations && (
            <div className="migration-warning">
              <div className="migration-header">
                <span className="migration-icon">⚠️</span>
                <strong>Pending Database Migrations</strong>
              </div>
              <div className="migration-list">
                {deploymentStatus.pending_migrations.map((migration, idx) => (
                  <div key={idx} className="migration-item">• {migration}</div>
                ))}
              </div>
              <div className="migration-note">
                Run migrations before restarting services to avoid errors.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Service Management */}
      <div className="service-management">
        <h4>Service Management</h4>
        
        {services.map(service => (
          <div key={service.name} className="service-row">
            <div className="service-info">
              <div className="service-name">
                <span className={`service-status ${service.status}`}>●</span>
                {service.name}
              </div>
              <div className="service-stats">
                <span>Uptime: {formatUptime(service.uptime)}</span>
                <span>•</span>
                <span>Restarts: {service.restarts}</span>
                <span>•</span>
                <span>CPU: {service.cpu}%</span>
                <span>•</span>
                <span>Memory: {formatMemory(service.memory)}</span>
              </div>
            </div>
            <button
              onClick={() => restartService(service.name)}
              disabled={restartingService === service.name}
              className="restart-btn"
            >
              {restartingService === service.name ? 'Restarting...' : '🔄 Restart'}
            </button>
          </div>
        ))}
        
        {services.length > 1 && (
          <div className="service-row all-services">
            <div className="service-info">
              <div className="service-name">
                <strong>All Services</strong>
              </div>
            </div>
            <button
              onClick={() => restartService('all')}
              disabled={restartingService === 'all'}
              className="restart-btn restart-all"
            >
              {restartingService === 'all' ? 'Restarting...' : '🔄 Restart All'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeploymentManagement;
